import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import { AppHeader } from '../../shared/components/AppComponents';
import BookSearch from './BookSearch';
import ClubDetail from './ClubDetail';
import ClubDiscovery from './ClubDiscovery';
import MyLibrary from './MyLibrary';
import MyBooks from './MyBooks';
import LearningTracks from './LearningTracks';
import Recommendations from './Recommendations';
import ReaderProfile from './ReaderProfile';
import StreaksChallenges from './StreaksChallenges';
import Achievements from './Achievements';
import LeaderboardView from './LeaderboardView';
import BookDNACard from './BookDNACard';
import BuddyReads from './BuddyReads';
import QuotesJournal from './QuotesJournal';
import TimerTBR from './TimerTBR';
import MoodDiscovery from './MoodDiscovery';
import Wishlist from './Wishlist';
import ReadingTimeline from './ReadingTimeline';
import './BookClubApp.css';

const TABS = [
  { id: 'discover', emoji: '🔍', key: 'discover' },
  { id: 'my-books', emoji: '📕', key: 'myBooks' },
  { id: 'my-library', emoji: '📖', key: 'library' },
  { id: 'tracks', emoji: '🛤️', key: 'tracks' },
  { id: 'mood', emoji: '🎭', key: 'mood' },
  { id: 'wishlist', emoji: '🎁', key: 'wishlist' },
  { id: 'recommendations', emoji: '⭐', key: 'forYou' },
  { id: 'streaks', emoji: '🔥', key: 'streaks' },
  { id: 'achievements', emoji: '🏆', key: 'trophies' },
  { id: 'buddy', emoji: '🤝', key: 'buddy' },
  { id: 'journal', emoji: '📓', key: 'journal' },
  { id: 'timeline', emoji: '📅', key: 'timeline' },
  { id: 'timer', emoji: '⏱️', key: 'timer' },
  { id: 'dna', emoji: '🧬', key: 'dna' },
  { id: 'leaderboard', emoji: '🏅', key: 'board' },
  { id: 'search', emoji: '🔎', key: 'search' },
  { id: 'profile', emoji: '👤', key: 'profile' },
];

function BookClubApp({ user }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [activeTab, setActiveTab] = useState('discover');
  const [factFictionMode, setFactFictionMode] = useState('fiction');
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [inviteMessage, setInviteMessage] = useState(null);

  // Handle invite token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite');
    if (!inviteToken) return;

    // Clear the query param from URL
    const url = new URL(window.location);
    url.searchParams.delete('invite');
    window.history.replaceState({}, '', url.pathname + url.hash);

    const acceptInvite = async () => {
      try {
        const data = await apiFetchJson(instance, `/api/bookclubs/invite/${inviteToken}/accept`, { method: 'POST' });
        setInviteMessage({ type: 'success', text: data.message || t('bookClub.invite.welcome') });
        if (data.clubId) setSelectedClubId(data.clubId);
      } catch (err) {
        setInviteMessage({ type: 'error', text: err.message || t('bookClub.invite.failedRetry') });
      }
    };
    acceptInvite();
  }, [instance]);

  // eslint-disable-next-line no-unused-vars
  const toggleMode = useCallback(() => {
    setFactFictionMode(prev => prev === 'fiction' ? 'fact' : 'fiction');
  }, []);

  // Cross-tab navigation callback — passed to all child components
  const navigateTo = useCallback((tabId, options = {}) => {
    setSelectedClubId(null);
    setActiveTab(tabId);
    if (options.mode) setFactFictionMode(options.mode);
  }, []);

  const renderTabContent = () => {
    if (selectedClubId) {
      return (
        <ClubDetail
          clubId={selectedClubId}
          onBack={() => setSelectedClubId(null)}
          factFictionMode={factFictionMode}
          navigateTo={navigateTo}
        />
      );
    }

    switch (activeTab) {
      case 'discover':
        return <ClubDiscovery factFictionMode={factFictionMode} onSelectClub={setSelectedClubId} navigateTo={navigateTo} />;
      case 'search':
        return <BookSearch factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'my-library':
        return <MyLibrary factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'my-books':
        return <MyBooks factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'tracks':
        return <LearningTracks factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'mood':
        return <MoodDiscovery factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'wishlist':
        return <Wishlist factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'recommendations':
        return <Recommendations factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'streaks':
        return <StreaksChallenges factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'achievements':
        return <Achievements factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'buddy':
        return <BuddyReads factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'journal':
        return <QuotesJournal factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'timeline':
        return <ReadingTimeline factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'timer':
        return <TimerTBR factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'dna':
        return <BookDNACard factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'leaderboard':
        return <LeaderboardView factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      case 'profile':
        return <ReaderProfile factFictionMode={factFictionMode} navigateTo={navigateTo} />;
      default:
        return null;
    }
  };

  return (
    <div className="bookclub-app">
      <AppHeader title={t('bookClub.title')} user={user} />

      {inviteMessage && (
        <div className={`bookclub-invite-banner ${inviteMessage.type}`} role="alert">
          <span>{inviteMessage.text}</span>
          <button onClick={() => setInviteMessage(null)} aria-label="Dismiss message">✕</button>
        </div>
      )}

      <div className="bookclub-mode-toggle" role="radiogroup" aria-label="Content mode">
        <button
          className={`bookclub-mode-btn ${factFictionMode === 'fiction' ? 'active' : ''}`}
          onClick={() => setFactFictionMode('fiction')}
          role="radio"
          aria-checked={factFictionMode === 'fiction'}
        >
          📖 {t('bookClub.fiction')}
        </button>
        <button
          className={`bookclub-mode-btn ${factFictionMode === 'fact' ? 'active' : ''}`}
          onClick={() => setFactFictionMode('fact')}
          role="radio"
          aria-checked={factFictionMode === 'fact'}
        >
          📚 {t('bookClub.fact')}
        </button>
      </div>

      <div className="bookclub-tabs" role="tablist" aria-label="Book club sections">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`bookclub-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setSelectedClubId(null); }}
            role="tab"
            aria-selected={activeTab === tab.id}
          >
            {tab.emoji} {t('bookClub.tabs.' + tab.key)}
          </button>
        ))}
      </div>

      <div className="bookclub-content" role="tabpanel">
        {renderTabContent()}
      </div>
    </div>
  );
}

export default BookClubApp;
